/**
 * useProfileHydration — state + hydration + auto-save + submit hook for the
 * ProfileSetup flow. Extracted from the ~1080-line page during the Feb 2026
 * refactor to keep step components dumb and testable.
 *
 * Responsibilities (in call order):
 *   1. Own the `profile` shape + step index.
 *   2. Hydrate from `/profile/draft` OR live `/profile` (edit mode).
 *   3. Debounced auto-save to `/profile/draft` on every change post-hydration.
 *   4. Photo upload flow (moderate-bytes → cropper → /upload).
 *   5. Reference list loading (prompts, interests, orientations, …).
 *   6. `submit()` → `/profile/setup` + redirect + toast.
 *   7. `exitOnboarding()` — flush pending draft then logout.
 *
 * Contract is stable; every data-testid remains identical to the previous
 * inline implementation to keep Playwright suites passing.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, api } from '@/contexts/AuthContext';
import { guessCountryFromLocale, flagFromCode } from '@/lib/countries';

const INITIAL_PROFILE = () => ({
  display_name: '',
  age: '',
  gender: '',
  bio: '',
  photos: [],
  answers: {},
  location: { city: '', country: guessCountryFromLocale()?.name || '', lat: 0, lng: 0 },
  radius: 50,
  race: '',
  body_type: '',
  diet: '',
  pets: '',
  politics: '',
  sexual_orientation: '',
  show_me_orientations: [],
  interests: [],
  religion: '',
  job_title: '',
  company: '',
  industry: '',
  education: '',
  school_name: '',
  languages: [],
  personality_type: '',
  zodiac_sign: '',
  exercise: '',
  height_cm: 170,
  prompts: [],
  hometown: { city: '', state: '', country: '' },
  looking_for: '',
  lifestyle_answers: {},
});

/** Field allowlist reused by both auto-save AND explicit setup POST. */
const draftPayload = (profile, step) => ({
  display_name: profile.display_name || null,
  age: profile.age ? parseInt(profile.age) : null,
  gender: profile.gender || null,
  bio: profile.bio || null,
  photos: profile.photos.length ? profile.photos : null,
  answers: Object.keys(profile.answers).length ? profile.answers : null,
  location: profile.location?.city ? profile.location : null,
  race: profile.race || null,
  body_type: profile.body_type || null,
  diet: profile.diet || null,
  pets: profile.pets || null,
  politics: profile.politics || null,
  sexual_orientation: profile.sexual_orientation || null,
  show_me_orientations: profile.show_me_orientations?.length ? profile.show_me_orientations : null,
  interests: profile.interests?.length ? profile.interests : null,
  religion: profile.religion || null,
  job_title: profile.job_title || null,
  company: profile.company || null,
  industry: profile.industry || null,
  education: profile.education || null,
  school_name: profile.school_name || null,
  languages: profile.languages?.length ? profile.languages : null,
  personality_type: profile.personality_type || null,
  zodiac_sign: profile.zodiac_sign || null,
  exercise: profile.exercise || null,
  height_cm: profile.height_cm || null,
  prompts: profile.prompts?.length ? profile.prompts : null,
  lifestyle_answers: Object.keys(profile.lifestyle_answers || {}).length ? profile.lifestyle_answers : null,
  step,
});

export default function useProfileHydration() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isExplicitEdit = new URLSearchParams(routerLocation.search).get('edit') === '1';
  const targetSection = new URLSearchParams(routerLocation.search).get('section');
  const { user, checkAuth, logout } = useAuth();

  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(() => ({
    ...INITIAL_PROFILE(),
    display_name: user?.name || '',
  }));
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [questions, setQuestions] = useState([]);

  // Reference dictionaries served by /api/*
  const [promptsList, setPromptsList] = useState([]);
  const [interestsList, setInterestsList] = useState([]);
  const [interestsCategories, setInterestsCategories] = useState([]);   // Feb 2026 · v2.2.0 grouped
  const [promptsCategories, setPromptsCategories] = useState([]);       // Feb 2026 · v2.3.0 grouped
  const [orientationsList, setOrientationsList] = useState([]);
  const [religionsList, setReligionsList] = useState([]);
  const [educationList, setEducationList] = useState([]);
  const [industriesList, setIndustriesList] = useState([]);
  const [languagesList, setLanguagesList] = useState([]);
  const [personalityList, setPersonalityList] = useState([]);
  const [zodiacList, setZodiacList] = useState([]);
  const [exerciseList, setExerciseList] = useState([]);
  const [politicsList, setPoliticsList] = useState([]);

  const fileRef = useRef(null);
  const draftHydratedRef = useRef(false);
  const draftSaveTimerRef = useRef(null);

  // ==================== 1. Redirect if already complete ====================
  useEffect(() => {
    if (!isExplicitEdit && user?.profile_complete && user?.profile) {
      navigate('/discover', { replace: true });
      return;
    }
    api.get('/compatibility/questions').then((r) => setQuestions(r.data.questions)).catch(() => {});
  }, [user, navigate, isExplicitEdit]);

  // ==================== 2. Hydrate from draft or live profile ====================
  useEffect(() => {
    let cancelled = false;

    const hydrateFromLive = async () => {
      try {
        const r = await api.get('/profile');
        if (cancelled) return;
        const p = r.data || {};
        setProfile((prev) => ({
          ...prev,
          display_name: p.display_name ?? prev.display_name,
          age: p.age ?? prev.age,
          gender: p.gender ?? prev.gender,
          bio: p.bio ?? prev.bio,
          voice_prompt: p.voice_prompt || null,     // Feb 2026 · voice-clip section
          photos: p.photos && p.photos.length ? p.photos : prev.photos,
          answers: p.answers || prev.answers,
          lifestyle_answers: p.lifestyle_answers || prev.lifestyle_answers,
          location: p.location && p.location.city ? p.location : prev.location,
          radius: p.location_radius ?? prev.radius,
          race: Array.isArray(p.race) ? p.race[0] || '' : (p.race || ''),
          body_type: p.body_type || '',
          diet: p.diet || '',
          pets: p.pets || '',
          politics: p.politics || '',
          sexual_orientation: p.sexual_orientation || '',
          show_me_orientations: p.show_me_orientations || [],
          interests: p.interests || [],
          religion: p.religion || '',
          job_title: p.job_title || '',
          company: p.company || '',
          industry: p.industry || '',
          education: p.education || '',
          school_name: p.school_name || '',
          languages: p.languages || [],
          personality_type: p.personality_type || '',
          zodiac_sign: p.zodiac_sign || '',
          exercise: p.exercise || '',
          height_cm: p.height_cm || prev.height_cm,
          prompts: p.prompts || prev.prompts,
          hometown: p.hometown || { city: '', state: '', country: '' },
          looking_for: p.looking_for || '',
        }));
      } catch (err) {
        console.warn('Edit hydration failed', err);
        toast.error("Couldn't load your profile — start with what you remember, we'll save on Continue");
      }
    };

    const hydrateFromDraft = () =>
      api.get('/profile/draft').then((r) => {
        if (cancelled) return;
        const d = r.data?.draft;
        if (d && typeof d === 'object') {
          setProfile((prev) => ({
            ...prev,
            ...Object.fromEntries(Object.entries(d).filter(([k]) => k !== 'step')),
            location: d.location || prev.location,
            answers: d.answers || prev.answers,
            lifestyle_answers: d.lifestyle_answers || prev.lifestyle_answers,
            photos: d.photos || prev.photos,
            prompts: d.prompts || prev.prompts,
          }));
          if (typeof d.step === 'number' && d.step >= 0 && d.step <= 4) setStep(d.step);
          toast.success('Welcome back — picked up where you left off');
        }
      }).catch(() => { /* no draft is fine */ });

    (async () => {
      if (isExplicitEdit && user?.profile_complete) await hydrateFromLive();
      else await hydrateFromDraft();
      draftHydratedRef.current = true;
    })();
    return () => { cancelled = true; };
  }, [isExplicitEdit, user?.profile_complete]);

  // ==================== 3. Debounced auto-save ====================
  useEffect(() => {
    if (!draftHydratedRef.current) return;
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      api.put('/profile/draft', draftPayload(profile, step))
        .catch(() => { /* best-effort */ });
    }, 800);
    return () => { if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current); };
  }, [profile, step]);

  // ==================== 4. Photo upload / crop ====================
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Select an image'); return; }
    if (profile.photos.length >= 6) { toast.error('Max 6 photos'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const mod = await api.post('/photos/moderate-bytes', fd);
      if (mod.data && mod.data.approved === false) {
        const label = mod.data.reason || 'not allowed';
        toast.error(`Photo can't be used — ${label} detected. Please try another.`);
        if (fileRef.current) fileRef.current.value = '';
        return;
      }
    } catch (err) {
      console.warn('photo pre-flight failed, proceeding to cropper:', err);
    } finally {
      setUploading(false);
    }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onCropComplete = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, `photo_${Date.now()}.jpg`);
      const r = await api.post('/upload', fd);
      setProfile((p) => ({ ...p, photos: [...p.photos, r.data.path] }));
      toast.success('Photo added!');
      setCropSrc(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removePhotoByPath = (path) =>
    setProfile((prev) => ({ ...prev, photos: prev.photos.filter((x) => x !== path) }));
  const reorderPhotos = (newOrder) =>
    setProfile((prev) => ({ ...prev, photos: newOrder }));

  const getUrl = useCallback((p) => {
    if (!p) return null;
    if (p.startsWith('http')) return p;
    return `${window.location.origin}/api/files/${p}`;
  }, []);

  // ==================== 5. Reference lists + optional section scroll ====================
  useEffect(() => {
    api.get('/prompts').then((r) => setPromptsList(r.data.prompts)).catch(() => {});
    api.get('/interests').then((r) => setInterestsList(r.data.interests || [])).catch(() => {});
    // Feb 2026 · v2.2.0 — categorized (6 sections). Falls back to flat list
    // if this endpoint is missing (older backend). The setup wizard renders
    // categories when present, otherwise the flat cloud.
    api.get('/interests/categories').then((r) => setInterestsCategories(r.data.categories || [])).catch(() => {});
    // Feb 2026 · v2.3.0 — categorized prompts (4 sections). Same fallback
    // pattern as interests — if the endpoint is missing, the picker keeps
    // rendering the flat 26-chip cloud.
    api.get('/prompts/categories').then((r) => setPromptsCategories(r.data.categories || [])).catch(() => {});
    if (targetSection) {
      const t = setTimeout(() => {
        const el = document.getElementById(`section-${targetSection}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
      return () => clearTimeout(t);
    }
    api.get('/orientations').then((r) => setOrientationsList(r.data.orientations || [])).catch(() => {});
    api.get('/religions').then((r) => setReligionsList(r.data.religions || [])).catch(() => {});
    api.get('/education-levels').then((r) => setEducationList(r.data.education_levels || [])).catch(() => {});
    api.get('/industries').then((r) => setIndustriesList(r.data.industries || [])).catch(() => {});
    api.get('/languages').then((r) => setLanguagesList(r.data.languages || [])).catch(() => {});
    api.get('/personality-types').then((r) => setPersonalityList(r.data.personality_types || [])).catch(() => {});
    api.get('/zodiac-signs').then((r) => setZodiacList(r.data.zodiac_signs || [])).catch(() => {});
    api.get('/exercise-frequencies').then((r) => setExerciseList(r.data.exercise_frequencies || [])).catch(() => {});
    api.get('/politics-options').then((r) => setPoliticsList(r.data.politics || [])).catch(() => {});
  }, [targetSection]);

  // ==================== 6. Submit ====================
  const submit = async () => {
    if (!profile.display_name || !profile.age || !profile.gender) {
      toast.error('Fill required fields');
      return;
    }
    setLoading(true);
    try {
      const cleanPrompts = (profile.prompts || [])
        .filter((p) => p && p.key && (p.answer || '').trim())
        .slice(0, 3)
        .map((p) => ({ key: p.key, answer: p.answer.trim() }));
      await api.post('/profile/setup', {
        display_name: profile.display_name,
        age: parseInt(profile.age),
        gender: profile.gender,
        bio: profile.bio,
        photo_url: profile.photos[0] || null,
        photos: profile.photos,
        answers: profile.answers,
        lifestyle_answers: profile.lifestyle_answers || {},
        location: profile.location.city ? profile.location : null,
        hometown: profile.hometown && profile.hometown.city ? profile.hometown : null,
        looking_for: profile.looking_for || null,
        race: profile.race || null,
        body_type: profile.body_type || null,
        diet: profile.diet || null,
        pets: profile.pets || null,
        politics: profile.politics || null,
        sexual_orientation: profile.sexual_orientation || null,
        show_me_orientations: profile.show_me_orientations || [],
        interests: profile.interests || [],
        religion: profile.religion || null,
        job_title: profile.job_title || null,
        company: profile.company || null,
        industry: profile.industry || null,
        education: profile.education || null,
        school_name: profile.school_name || null,
        languages: profile.languages || [],
        personality_type: profile.personality_type || null,
        zodiac_sign: profile.zodiac_sign || null,
        exercise: profile.exercise || null,
        height_cm: profile.height_cm || null,
        prompts: cleanPrompts,
      });
      await checkAuth();
      toast.success(isExplicitEdit ? 'Profile updated!' : 'Profile created!');
      navigate(isExplicitEdit ? '/profile' : '/discover', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  // ==================== 7. Exit onboarding (flush + logout) ====================
  const exitOnboarding = async () => {
    if (!window.confirm("Save & exit? Your progress is saved — sign back in any time to pick up right where you left off.")) return;
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      try { await api.put('/profile/draft', draftPayload(profile, step)); }
      catch (err) { console.warn('final draft flush failed', err); }
    }
    try { await logout(); }
    catch (err) { console.warn('logout failed', err); }
    navigate('/auth', { replace: true });
  };

  // ==================== 8. Location detection ====================
  const detectLoc = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const lat = p.coords.latitude;
        const lng = p.coords.longitude;
        setProfile((s) => ({ ...s, location: { ...s.location, lat, lng } }));
        toast.success('Location detected!');
        try {
          const r = await api.get(`/geo/reverse?lat=${lat}&lng=${lng}`);
          const d = r.data || {};
          setProfile((s) => ({
            ...s,
            location: {
              ...s.location,
              city: d.city || s.location.city,
              country: d.country || s.location.country,
              lat,
              lng,
            },
          }));
          if (d.country) {
            const flag = flagFromCode(d.country_code);
            const bits = [d.city, d.country].filter(Boolean).join(', ');
            toast.success(`📍 Detected: ${bits} ${flag}`.trim());
          }
        } catch { /* silent — manual entry backup */ }
      },
      () => toast.error('Could not detect'),
    );
  };

  return {
    // routing/context
    isExplicitEdit,
    // state + setters
    profile, setProfile, step, setStep,
    loading, uploading, cropSrc, setCropSrc,
    // ref for hidden file input in Step 0
    fileRef,
    // reference dictionaries
    questions,
    promptsList, interestsList, interestsCategories, promptsCategories, orientationsList, religionsList, educationList,
    industriesList, languagesList, personalityList, zodiacList, exerciseList, politicsList,
    // handlers
    uploadPhoto, onCropComplete, removePhotoByPath, reorderPhotos, getUrl,
    submit, exitOnboarding, detectLoc,
  };
}
